import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { normalizePartnerName } from "@/lib/normalizeName";

type Kind = "proveedor" | "cliente";

interface SimilarRow {
  id: string;
  nombre: string;
  similarity_score: number;
}

/**
 * Hook que llama al RPC find_similar_proveedores / find_similar_clientes
 * con debounce ligero. Solo se activa cuando el caller lo habilita
 * (típicamente al renderizar <CommandEmpty>).
 */
export function useSimilarPartners(kind: Kind, search: string, enabled: boolean) {
  const { orgId } = useAuth();
  const [data, setData] = useState<SimilarRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const normalized = normalizePartnerName(search);
    if (!enabled || !orgId || normalized.length < 3) {
      setData([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const t = setTimeout(async () => {
      const fn = kind === "proveedor" ? "find_similar_proveedores" : "find_similar_clientes";
      const { data: rows, error } = await supabase.rpc(fn as any, {
        search_name: search.trim(),
        p_org_id: orgId,
      });
      if (cancelled) return;
      if (error) {
        setData([]);
      } else {
        setData((rows as SimilarRow[]) ?? []);
      }
      setLoading(false);
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [kind, search, enabled, orgId]);

  return { data, loading };
}
